import React, { useState, useEffect, useRef } from 'react';
import { FincluOperationStatus } from '@/types/FincluOperationStatus';
import { FincluStatusBadge } from './FincluStatusBadge';
import { useTranslations } from 'use-intl';
import { ChevronDown } from 'react-feather';

interface FincluStatusFilterProps {
	selectedStatuses: FincluOperationStatus[];
	onStatusChange: (statuses: FincluOperationStatus[]) => void;
}

export const FincluStatusFilter: React.FC<FincluStatusFilterProps> = ({
	selectedStatuses,
	onStatusChange
}) => {
	const t = useTranslations('SupportClients');
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleStatusFilter = (status: FincluOperationStatus) => {
		const newStatuses = selectedStatuses.includes(status)
			? selectedStatuses.filter((s) => s !== status)
			: [...selectedStatuses, status];
		onStatusChange(newStatuses);
	};

	const statuses = [
		FincluOperationStatus.IN_PROGRESS,
		FincluOperationStatus.PENDING_REVIEW,
		FincluOperationStatus.APPROVED,
		FincluOperationStatus.REJECTED
	];

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded px-2 py-1"
			>
				<span>
					{selectedStatuses.length > 0
						? `${selectedStatuses.length} ${t('Selected')}`
						: t('FincluStatusFilterLabel')}
				</span>
				<ChevronDown size={12} />
			</button>
			{isOpen && (
				<div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[220px]">
					{statuses.map((status) => (
						<label
							key={status}
							className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
						>
							<input
								type="checkbox"
								checked={selectedStatuses.includes(status)}
								onChange={() => handleStatusFilter(status)}
								className="cursor-pointer"
							/>
							<FincluStatusBadge status={status} size="sm" />
						</label>
					))}
				</div>
			)}
		</div>
	);
};
